import { BrandKit } from '@goldcast/api/backend';
import Icon from '@/components/atoms/Icon';
import { PlatformBrandKitImage } from '@/Pages/BrandKit/types';

/**
 * Small brand-kit thumbnail used in the simplified-upload Brand Kit dropdown (trigger + options).
 *
 * Mirrors the `/brand-kit` management table thumbnail (admin-app `BrandKitThumbnail.vue`): prefer the
 * first design element, fall back to the first logo, and when the kit has neither image, render the
 * same neutral placeholder (palette icon on a bordered tile) the table shows for a thumbnail-less kit.
 * Platform brand-kit `logos`/`design_elements` are typed as `{}` on the generated `BrandKit`; they are
 * arrays of image files with absolute `url`s (consumed the same way elsewhere, e.g. the media picker).
 */
export default function BrandKitThumbnail({
  brandKit,
  className = ''
}: {
  brandKit: BrandKit;
  className?: string;
}): JSX.Element {
  const designElements = (brandKit.design_elements as PlatformBrandKitImage[] | undefined) ?? [];
  const logos = (brandKit.logos as PlatformBrandKitImage[] | undefined) ?? [];
  const imageUrl = designElements[0]?.url ?? logos[0]?.url;
  // Quote the url() value so keys with spaces or parentheses (e.g. S3 keys) don't break the CSS rule;
  // escape any single quote in the URL so it can't terminate the quoted value early.
  const backgroundImage = imageUrl ? `url('${imageUrl.replace(/'/g, '%27')}')` : undefined;

  return (
    <div
      data-testid={`brand-kit-thumbnail-${brandKit.id}`}
      className={`relative size-9 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-white ${className}`}
    >
      {imageUrl ? (
        <div
          data-testid={`brand-kit-thumbnail-image-${brandKit.id}`}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage }}
        />
      ) : (
        <div
          data-testid={`brand-kit-thumbnail-fallback-${brandKit.id}`}
          className="absolute inset-0 flex items-center justify-center"
        >
          <Icon name="IconPalette" size={16} stroke={1.75} className="text-slate-400" />
        </div>
      )}
    </div>
  );
}
